import { noop, throttle } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import CookieManager from 'js-cookie';
import styled from 'styled-components';
import moment from 'moment';
import { white } from '@zocdoc/branding/colors';
import * as Layout from '@zocdoc/components/src/Layout';
import * as SEO from '@zocdoc/components/src/Seo';
import { Breakpoints, breakpoint, getWindowBreakpoint } from '@zocdoc/components/src/grid';
import { FontFamily } from '@zocdoc/components/src/typography';
import { CrossPageActionTracker } from '../../Shared/CrossPageActionTracking';
import TrackingPixel from '../../config/tracking';
import InsuranceCta from './InsuranceCta';
import SalpBlock from './SalpBlock';
import SalpHeader from './SalpHeader';
import SearchBar from './SearchBar';
import SearchResultsWithMap from './SearchResultsWithMap';
import { CrazyEgg } from '../../Shared/CrazyEgg';
import { COOKIES } from '../../constants';
import * as ViewConstants from './constants';
import { getWellGuideLinkVariantFromProps } from '../../config/ab/selector';

export default class SearchPage extends PureComponent {
    static propTypes = {
        ab: PropTypes.shape({
            experimentId: PropTypes.string,
            defaultAssignment: PropTypes.string,
            isFeatureFlag: PropTypes.bool,
        }),
        carrierId: PropTypes.any,
        error: PropTypes.any,
        focusInsurancePicker: PropTypes.func,
        governmentInsuranceData: PropTypes.shape({
            showGovernmentInsuranceWarning: PropTypes.bool,
            header: PropTypes.string,
            links: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string,
                    url: PropTypes.string,
                })
            ),
            message: PropTypes.string,
        }),
        loading: PropTypes.bool,
        pageSize: PropTypes.number,
        parameters: PropTypes.shape({
            carrierId: PropTypes.string,
        }).isRequired,
        refetch: PropTypes.func,
        searchOrigin: PropTypes.shape({
            latitude: PropTypes.number,
            longitude: PropTypes.number,
        }),
        seo: PropTypes.PropTypes.shape({
            header: PropTypes.any,
            related: PropTypes.any,
            blurb: PropTypes.shape({
                description: PropTypes.string,
                header: PropTypes.string,
            }),
            breadcrumbs: PropTypes.any,
            salpHeader: PropTypes.shape({
                title: PropTypes.string,
                caption: PropTypes.string,
            }),
            salpBlock: PropTypes.shape({
                section1: PropTypes.string,
                section2: PropTypes.string,
                section3: PropTypes.string,
            }),
        }),
        shouldShowSalpBlock: PropTypes.bool,
        shouldShowSalpSeoContent: PropTypes.bool,
        shouldShowWellGuideLink: PropTypes.bool,
        telehealthAds: PropTypes.array,
        theme: PropTypes.object,
        totalCount: PropTypes.number,
        trackClickJoin: PropTypes.func.isRequired,
        trackClickSignIn: PropTypes.func.isRequired,
        trackPageHeaderCTALinkClick: PropTypes.func.isRequired,
        trackPageHeaderLogoClick: PropTypes.func.isRequired,
        trackClickSignInDropdown: PropTypes.func,
        trackClickSignInDropdownLink: PropTypes.func,
        trackSignInLinkClickConversion: PropTypes.func,
    };

    static defaultProps = {
        ab: {},
        focusInsurancePicker: noop,
        pageSize: 10,
        refetch: noop,
    };

    state = {
        searchBarExpanded: false,
    };

    componentDidMount() {
        /*
        * Note: important business logic. When a user conducts a search, they will be considered
        * A new patient for any doctor they book within a day. We are using this information
        * to determine whether to charge users in a flapjack model. Set cookie to timestamp based
        * on when it should expire, because we can't trust that cetain browsers (Chrome) will
        * respect cookie expiration settings
        */
        CookieManager.set(
            COOKIES.isNewPatient.name,
            moment().add(COOKIES.isNewPatient.expiration, 'days').toISOString(),
            { expires: COOKIES.isNewPatient.expiration },
        );

        window.addEventListener('resize', this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize, true);
    }

    render() {
        const {
            refetch,
            searchOrigin,
            shouldShowWellGuideLink,
            trackClickSignInDropdown,
            trackClickSignInDropdownLink,
            trackPageHeaderCTALinkClick,
            trackSignInLinkClickConversion,
        } = this.props;

        return (
            <StyledPage>
                <CrossPageActionTracker />
                <Layout.Header
                    onCTALinkClick={trackPageHeaderCTALinkClick}
                    onCustomLinkClick={this.trackClickHeaderCustomLink}
                    onSignInDropdownClick={trackClickSignInDropdown}
                    onSignInDropdownLinkClick={trackClickSignInDropdownLink}
                    showNavigationLinks
                    showWellGuideLink={shouldShowWellGuideLink}
                    trackSignInLinkClickConversion={trackSignInLinkClickConversion}
                    useSignInDropdown
                    wellGuideLinkVariant={getWellGuideLinkVariantFromProps(this.props)}
                >
                    <StyledSearchBar
                        collapse={this.collapseSearchBar}
                        data-test="search-bar"
                        expand={this.expandSearchBar}
                        forceSubmitSearch={refetch}
                        isExpanded={this.state.searchBarExpanded}
                        searchOrigin={searchOrigin}
                    />
                </Layout.Header>

                {this.renderMain()}

                <Layout.Footer />

                <TrackingPixel />
                <CrazyEgg
                    DesktopSnapshotName="1Z Search - Desktop"
                    MobileSnapshotName="1Z Search - MobileWeb"
                    TabletSnapshotName="1Z Search - Tablet"
                />
            </StyledPage>
        );
    }

    renderMain() {
        const {
            doctorLocationIds,
            governmentInsuranceData,
            insuranceCarrierImage,
            loading,
            error,
            facets,
            page,
            pageCount,
            providers,
            providersMappedByNetworkType,
            searchRequestId,
            seo,
            shouldRenderReviewCount,
            telehealthAds,
            totalCount,
            visitReasons,
        } = this.props;

        return (
            <div>
                {this.maybeRenderSeoHeadings()}

                <SearchResultsWithMap
                    doctorLocationIds={doctorLocationIds}
                    error={error}
                    facets={facets}
                    governmentInsuranceData={governmentInsuranceData}
                    insuranceCarrierImage={insuranceCarrierImage}
                    loading={loading}
                    page={page}
                    pageCount={pageCount}
                    providers={providers}
                    providersMappedByNetworkType={providersMappedByNetworkType}
                    searchRequestId={searchRequestId}
                    seo={seo}
                    shouldRenderReviewCount={shouldRenderReviewCount}
                    showInsurancePicker={this.showInsurancePicker}
                    telehealthAds={telehealthAds}
                    totalCount={totalCount}
                    visitReasons={visitReasons}
                />

                {this.maybeRenderSalpBlock()}
                {this.maybeRenderSeoLinks()}
                {this.maybeRenderSeoBreadcrumbs()}
            </div>
        );
    }

    maybeRenderSeoHeadings() {
        const {
            parameters,
            seo,
            shouldShowSalpSeoContent,
            theme,
        } = this.props;

        if (seo) {
            const { header, salpHeader } = seo;
            const hasSalpHeader = salpHeader && shouldShowSalpSeoContent;
            const hasSeoHeader = theme.components.SeoHeader.enabled;

            return (
                <div>
                    {hasSalpHeader && (
                        <StyledSalpSeoBlurbBar
                            component={SEO.SeoBlurb}
                            data-test="search-salp-header"
                            description={salpHeader.caption}
                            header={salpHeader.title}
                            headerComponent={SalpHeader}
                        />
                    )}
                    {hasSeoHeader && (
                        <StyledSeoHeaderBar>
                            <StyledSeoHeader
                                data-test="search-seo-header"
                                headingLevel={hasSalpHeader ? 2 : 1}
                                onInsuranceClick={this.expandSearchBar}
                                onLocationClick={this.expandSearchBar}
                                sections={header}
                            />
                            <StyledInsuranceCta
                                data-test="insurance-cta"
                                hidden={parameters.carrierId !== '-1'}
                                onClick={this.handleInsuranceCtaClick}
                            />
                        </StyledSeoHeaderBar>
                    )}
                </div>
            );
        }
    }

    maybeRenderSeoLinks() {
        const { theme, seo } = this.props;

        if (seo && theme.components.SeoRelatedLinks.enabled) {
            const { related } = seo;
            return (
                <SeoRelatedLinksFooter>
                    <SEO.SeoRelatedLinks
                        data-test="search-seo-links"
                        sections={related.sections}
                    />
                </SeoRelatedLinksFooter>
            );
        }
    }

    maybeRenderSeoBreadcrumbs() {
        const { theme, seo } = this.props;

        if (seo && seo.breadcrumbs && theme.components.SeoBreadcrumbLinks.enabled) {
            const { breadcrumbs } = seo;
            return (
                <SeoBreadcrumbsFooter>
                    <SEO.SeoBreadcrumbs
                        data-test="search-seo-breadcrumbs"
                        links={breadcrumbs}
                    />
                </SeoBreadcrumbsFooter>
            );
        }
    }

    maybeRenderSalpBlock() {
        const { seo, shouldShowSalpBlock } = this.props;
        if (shouldShowSalpBlock && seo && seo.salpBlock) {
            return (
                <SalpBlock
                    block={seo.salpBlock}
                    data-test="search-salp-block"
                />
            );
        }
    }

    handleResize = throttle(() => {
        if (this.state.searchBarExpanded && getWindowBreakpoint() >= Breakpoints.medium) {
            this.setState({ searchBarExpanded: false });
        }
    }, 500);

    handleInsuranceCtaClick = () => {
        if (window.innerWidth < Breakpoints.medium) {
            this.expandSearchBar();
        } else {
            this.props.focusInsurancePicker();
        }
    };

    trackClickHeaderCustomLink = async e => {
        // This is duplicate code from Profile/Page/Components/view.js
        // Should find common solution as part of grander metrics cleanup
        const { target } = e;
        const url = target.getAttribute('href');

        if (url.indexOf('signin') !== -1) {
            await this.props.trackClickSignIn();
        } else if (url.indexOf('join') !== -1) {
            await this.props.trackClickJoin();
        }
    };

    collapseSearchBar = () => this.setState({ searchBarExpanded: false })
    expandSearchBar = () => this.setState({ searchBarExpanded: true })

    showInsurancePicker = () => {
        this.expandSearchBar();
        this.props.focusInsurancePicker();
    }
}

const StyledPage = styled(Layout.Page)`
    ${Layout.HeaderPrimitives.BottomBorder} {
        display: none;
    }
`;

const StyledSearchBar = styled(SearchBar)`
    margin-top: ${ViewConstants.HEADER_SEARCH_BAR_TOP_MARGIN}px;
`;

const StyledSeoHeaderBar = styled(Layout.PaddedContainer)`
    background-color: ${p => p.theme.components.SeoHeader.bgColor};
    padding-bottom: 12px;
    padding-top: 12px;

    ${breakpoint.small} {
        padding-bottom: 20px;
        padding-top: 20px;
    }
`;

const HEADER_FONT_SIZE_XXSMALL = 15;
const HEADER_FONT_SIZE_MEDIUM = 16;

const StyledSeoHeader = styled(SEO.SeoHeader)`
    display: inline;
    font: ${HEADER_FONT_SIZE_XXSMALL}px/1.5 ${FontFamily.SharpSansSemibold};
    vertical-align: middle;

    ${breakpoint.medium} {
        font-size: ${HEADER_FONT_SIZE_MEDIUM}px;
    }
`;

const StyledInsuranceCta = styled(InsuranceCta)`
    vertical-align: middle;
    font: ${HEADER_FONT_SIZE_XXSMALL}px/1.5 ${FontFamily.SharpSansSemibold};

    ${breakpoint.medium} {
        font-size: ${HEADER_FONT_SIZE_MEDIUM}px;
    }
`;

const SeoRelatedLinksFooter = styled.div`
    background-color: ${white};
`;

const SeoBreadcrumbsFooter = styled(Layout.PaddedContainer)`
    align-items: center;
    background-color: white;
    display: flex;
    height: 96px;
`;

const StyledSalpSeoBlurbBar = styled(Layout.PaddedContainer)`
    background-color: ${p => p.theme.header.bgColor};
    padding-bottom: 16px;
    p {
        font: 14px/20px ${FontFamily.KievitSlabBook};
        font-weight: 500;
        width: 100%;
        ${breakpoint.small} {
            font-size: 18px;
            line-height: 28px;
        }
    }

    ${breakpoint.small} {
        padding-bottom: 32px;
        padding-top: 12px;
    }
`;
